import { useEffect, useRef, useState } from "react";
import apiCall from "../network/api";
import { ObcOverview } from "../components/ObcOverview";
import { Navbar } from "../components/Navbar";
import { Routes, Route, useNavigate, useLocation } from "react-router-dom";
import { DocumentList } from "../components/DocumentList";
import { loader } from "../utils/svg";
import { ObcProfile } from "../components/ObcProfile";
import { EmptyAssignment } from "../components/EmptyAssignment";
import { Milestone } from "../components/Milestone";
import NoLocation from "./NoLocation";
import { SHIPMENT_MODE } from "../utils/constants";
import { getAuthItem, clearAuthData } from "../utils/indexDB";
import { clearCachedAccessToken } from "../network/api";
import { ObcBankDetails } from "../components/ObcBankDetails";

function ObcApp() {
  const [overviewData, setOverviewData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [filteredOverviewData, setFilteredOverviewData] = useState([]);
  const [status, setStatus] = useState("");
  const [isLocation, setIsLocation] = useState(true);
  // eslint-disable-next-line no-unused-vars
  const [userData, setUserData] = useState(null);
  const coordRef = useRef({});
  const [shipmentMode, setShipmentMode] = useState(SHIPMENT_MODE.AIR);

  const navigate = useNavigate();
  const location = useLocation();

  const [submitStatus, setSubmitStatus] = useState("disabled"); // 'disabled' | 'in-progress' | 'success' | 'failed'
  const [emailStatus, setEmailStatus] = useState("disabled"); // 'disabled' | 'in-progress' | 'success' | 'failed'

  const [submitAttempts, setSubmitAttempts] = useState(0);
  const [emailAttempts, setEmailAttempts] = useState(0);

  const [fetchLatestData, setFetchLatestData] = useState(false);

  //dev_release

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if ("geolocation" in navigator) {
      // Try to get the user's location
      navigator.geolocation.getCurrentPosition(
        (position) => {
          // Successfully got the location
          setIsLocation(true);
          coordRef.current = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          };
        },
        (error) => {
          // Error occurred, likely due to permission denied or other issues
          setIsLocation(false);
        }
      );
    } else {
      setIsLocation(false);
    }
  });

  const [userEmail, setUserEmail] = useState(null);
  const [userId, setUserId] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      const token = await getAuthItem("accessToken");
      const email = await getAuthItem("userEmail");
      const userId = await getAuthItem("userId");

      setUserEmail(email);
      setUserId(userId);

      if (!overviewData || overviewData?.length === 0 || fetchLatestData) {
        if (token && userId) {
          try {
            setFetchLatestData(false);
            const shipmentRes = await apiCall({
              endpoint: `/shipments/by-obc-email/${email}`,
              method: "GET",
            });

            // Try fetching signup row, but don't block loading on failure
            try {
              const signupRes = await apiCall({
                endpoint: "/otp/get-signup-row",
                method: "GET",
                params: { email },
              });

              if (signupRes?.data) setUserData(signupRes.data);
            } catch (signupErr) {
              console.warn(
                "Signup row fetch failed (non-blocking):",
                signupErr
              );
            }

            setOverviewData(shipmentRes?.shipments || []);
          } catch (err) {
            console.error("Error fetching shipment:", err);
            setError(err);
            if (err?.status === 403 || err?.status === 401) {
              clearCachedAccessToken();
              await clearAuthData();
              localStorage.clear();
              navigate("/login");
            }
          } finally {
            // Always stop loading regardless of success or error
            setLoading(false);
          }
        } else {
          navigate("/login");
        }
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchLatestData]);

  useEffect(() => {
    if (overviewData !== null && isLocation) {
      const normalizedEmail = userEmail ? userEmail.toLowerCase().trim() : "";

      // Active shipments where current user is the current active courier
      const activeShipments = overviewData?.filter((data) => {
        const stageObcEmail = (
          data?.stages?.[0]?.obc_email ||
          data?.stages?.[0]?.courier_assignments?.find((c) => c?.status === "active")?.obc_email ||
          (typeof data?.stages?.[0]?.courier_assignments?.find((c) => c?.status === "active")?.obc_id === "object"
            ? data?.stages?.[0]?.courier_assignments?.find((c) => c?.status === "active")?.obc_id?.email
            : null)
        )?.toLowerCase()?.trim();
        return !normalizedEmail || stageObcEmail === normalizedEmail;
      });

      // Transferred shipments where current user was previously assigned and transferred TODAY
      const transferredShipments = overviewData?.filter((data) => {
        const stageObcEmail = (
          data?.stages?.[0]?.obc_email ||
          data?.stages?.[0]?.courier_assignments?.find((c) => c?.status === "active")?.obc_email ||
          (typeof data?.stages?.[0]?.courier_assignments?.find((c) => c?.status === "active")?.obc_id === "object"
            ? data?.stages?.[0]?.courier_assignments?.find((c) => c?.status === "active")?.obc_id?.email
            : null)
        )?.toLowerCase()?.trim();
        const wasAssignedAndTransferredToday = data?.stages?.[0]?.courier_assignments?.some((c) => {
          const cEmail = (
            c?.obc_email ||
            (typeof c?.obc_id === "object" ? c?.obc_id?.email : null)
          )?.toLowerCase()?.trim();
          if (cEmail !== normalizedEmail || c?.status !== "transferred") {
            return false;
          }
          const transferDate = c?.unassigned_at;
          return (
            transferDate &&
            new Date(transferDate).toDateString() === new Date().toDateString()
          );
        });
        return normalizedEmail && stageObcEmail !== normalizedEmail && wasAssignedAndTransferredToday;
      });

      let milestoneData = activeShipments?.filter(
        (data) =>
          data?.stages?.[0]?.stage_status?.toLowerCase() === "in progress"
      );

      let startData = activeShipments?.filter(
        (data) => data?.stages?.[0]?.stage_status?.toLowerCase() === "pending"
      );

      let completedData = activeShipments?.filter(
        (data) =>
          data?.stages?.[0]?.stage_status?.toLowerCase() === "completed" &&
          new Date(data?.stages?.[0]?.end_date).toDateString() ===
          new Date().toDateString()
      );

      let emptyData = overviewData?.filter(
        (data) =>
          data?.start_date !== undefined &&
          data?.end_date !== undefined &&
          data?.end_date !== new Date().toISOString()?.split("T")?.[0]
      );

      if (milestoneData?.length > 0) {
        setStatus("milestone");
        setFilteredOverviewData(milestoneData);
        setShipmentMode(milestoneData[0].stages?.[0]?.mode);
        let currentStep =
          milestoneData[0]?.stages?.[0]?.milestones?.filter(
            (milestone) =>
              milestone.status === "completed" || milestone.status === "skipped"
          )?.length || 0;
        navigate(
          `/home/${milestoneData?.[0]?.uniqueId}/milestone?m=${currentStep + 1}`
        );
      } else if (startData?.length > 0) {
        setStatus("overview");
        setShipmentMode(startData[0].stages?.[0]?.mode);
        setFilteredOverviewData(startData);
        navigate(`/${startData?.[0]?.uniqueId}/overview`);
      } else if (completedData?.length > 0) {
        setStatus("completed");
        setShipmentMode(completedData[0].stages?.[0]?.mode);
        setFilteredOverviewData(completedData);
        navigate(`/${completedData?.[0]?.uniqueId}/completed`);
      } else if (transferredShipments?.length > 0) {
        setStatus("transferred");
        setShipmentMode(transferredShipments[0].stages?.[0]?.mode);
        setFilteredOverviewData(transferredShipments);
        navigate(`/${transferredShipments?.[0]?.uniqueId}/transferred`);
      } else {
        setStatus("noAssignment");
        setFilteredOverviewData(emptyData);
        navigate(`/noAssignment`);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overviewData, userEmail]);

  useEffect(() => {
    if (!isLocation) {
      navigate("/noLocation");
    } else if (location.pathname === "/noLocation") {
      navigate("/home");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLocation, location.pathname]);

  if (loading)
    return (
      <div className="h-screen flex justify-center items-center">
        {loader()}
      </div>
    );
  if (error) return <p>Error: {error.message}</p>;

  const handleSubmit = async ({ ...data }) => {
    setSubmitLoading(true);
    try {
      const result = await apiCall({
        endpoint: `/shipments/${filteredOverviewData?.[0]?._id}`,
        method: "PUT",
        data: data,
      });
      return result;
    } catch (err) {
      if (err?.data?.code === "COURIER_TRANSFERRED") {
        // Courier was reassigned mid-session: re-fetch so the existing
        // active/transferred classification in the effect above routes
        // to the Transferred screen cleanly, with no manual navigation
        // or interim failure/retry UI (avoids the flicker).
        setFetchLatestData(true);
        const transferredError = new Error("COURIER_TRANSFERRED");
        transferredError.code = "COURIER_TRANSFERRED";
        throw transferredError;
      }
      if (err?.data?.code === "SHIPMENT_CANCELLED") {
        // Re-fetch so the cancelled shipment drops out of the courier's view.
        setFetchLatestData(true);
        const cancelledError = new Error("SHIPMENT_CANCELLED");
        cancelledError.code = "SHIPMENT_CANCELLED";
        throw cancelledError;
      }
      setSubmitStatus("failed");
      if (err?.status === 403 || err?.status === 401) {
        clearCachedAccessToken();
        await clearAuthData();
        localStorage.clear();
        navigate("/login");
      }
      throw new Error(err);
    }
  };

  const sendMilesStoneUpdateEmail = async (data) => {
    try {
      const result = await apiCall({
        endpoint: "/overview",
        method: "POST",
        data: data,
      });

      return result;
    } catch (err) {
      if (err?.data?.code === "COURIER_TRANSFERRED") {
        setFetchLatestData(true);
        const transferredError = new Error("COURIER_TRANSFERRED");
        transferredError.code = "COURIER_TRANSFERRED";
        throw transferredError;
      }
      if (err?.data?.code === "SHIPMENT_CANCELLED") {
        setFetchLatestData(true);
        const cancelledError = new Error("SHIPMENT_CANCELLED");
        cancelledError.code = "SHIPMENT_CANCELLED";
        throw cancelledError;
      }
      setEmailStatus("failed");
      if (err?.status === 403 || err?.status === 401) {
        clearCachedAccessToken();
        await clearAuthData();
        localStorage.clear();
        navigate("/login");
      }
      throw new Error(err);
    }
  };
  let currentStep =
    filteredOverviewData?.[0]?.stages?.[0]?.milestones?.length || 0;

  return (
    <div>
      <Navbar
        homeRoute={
          status === "noAssignment"
            ? `/${status}`
            : status === "milestone"
              ? `/home/${filteredOverviewData?.[0]?.uniqueId}/milestone?m=${currentStep + 1
              }`
              : `/${filteredOverviewData?.[0]?.uniqueId}/${status}`
        }
        showOptions={isLocation}
      />

      <div>
        <Routes>
          <Route
            path={`/${filteredOverviewData?.[0]?.uniqueId}/overview`}
            element={
              <ObcOverview
                userEmail={userEmail}
                userId={userId}
                overviewData={filteredOverviewData?.[0]}
                handleSubmit={handleSubmit}
                submitLoading={submitLoading}
                status={status}
                setStatus={setStatus}
                setIsLocation={setIsLocation}
                coordRef={coordRef}
                name={userData?.firstname}
                isGround={shipmentMode === SHIPMENT_MODE.GROUND}
                selectedAogMilestoneObject={
                  filteredOverviewData?.[0]?.stages?.[0]?.milestones
                }
                setOverviewData={setOverviewData}
                sendMilesStoneUpdateEmail={sendMilesStoneUpdateEmail}
                setSubmitLoading={setSubmitLoading}
                setLoading={setLoading}
                emailStatus={emailStatus}
                setEmailStatus={setEmailStatus}
                submitStatus={submitStatus}
                setSubmitStatus={setSubmitStatus}
                emailAttempts={emailAttempts}
                setEmailAttempts={setEmailAttempts}
                submitAttempts={submitAttempts}
                setSubmitAttempts={setSubmitAttempts}
              />
            }
          />
          <Route
            path={`/noAssignment`}
            element={
              <EmptyAssignment
                name={userData?.firstname}
                message={
                  "You will be able to view your assignment once its assigned to you."
                }
              />
            }
          />
          <Route
            path={`/${filteredOverviewData?.[0]?.uniqueId}/completed`}
            element={
              <ObcOverview
                userEmail={userEmail}
                overviewData={
                  filteredOverviewData?.[filteredOverviewData?.length - 1]
                }
                handleSubmit={handleSubmit}
                submitLoading={submitLoading}
                status={status}
                setIsLocation={setIsLocation}
                coordRef={coordRef}
                name={userData?.firstname}
              />
            }
          />
          <Route
            path={`/${filteredOverviewData?.[0]?.uniqueId}/transferred`}
            element={
              <ObcOverview
                userEmail={userEmail}
                userId={userId}
                overviewData={filteredOverviewData?.[0]}
                handleSubmit={handleSubmit}
                submitLoading={submitLoading}
                status="transferred"
                setIsLocation={setIsLocation}
                coordRef={coordRef}
                name={userData?.firstname}
              />
            }
          />
          <Route
            path={`/${filteredOverviewData?.[0]?.uniqueId}/milestone`}
            element={
              <Milestone
                userEmail={userEmail}
                overviewData={filteredOverviewData?.[0]}
                selectedAogMilestoneObject={
                  filteredOverviewData?.[0]?.stages?.[0]?.milestones
                }
                setOverviewData={setOverviewData}
                handleSubmit={handleSubmit}
                sendMilesStoneUpdateEmail={sendMilesStoneUpdateEmail}
                submitLoading={submitLoading}
                setStatus={setStatus}
                setSubmitLoading={setSubmitLoading}
                setLoading={setLoading}
                setIsLocation={setIsLocation}
                coordRef={coordRef}
                emailStatus={emailStatus}
                setEmailStatus={setEmailStatus}
                submitStatus={submitStatus}
                setSubmitStatus={setSubmitStatus}
                emailAttempts={emailAttempts}
                setEmailAttempts={setEmailAttempts}
                submitAttempts={submitAttempts}
                setSubmitAttempts={setSubmitAttempts}
                isGround={shipmentMode === SHIPMENT_MODE.GROUND}
                setFetchLatestData={setFetchLatestData}
              />
            }
          />
          <Route path="/noLocation" element={<NoLocation />} />

          <Route
            path="/documents"
            element={
              <DocumentList
                assignmentId={filteredOverviewData?.[0]?.uniqueId}
                rowId={filteredOverviewData?.[0]?.id}
                status={status}
                name={userData?.firstname}
                documents={
                  filteredOverviewData?.[0]?.stages?.[0]?.documents_by_admin
                }
              />
            }
          />

          <Route
            path="/profile"
            element={
              <ObcProfile
                name={
                  filteredOverviewData?.[0]?.obc_first_name?.value +
                  " " +
                  filteredOverviewData?.[0]?.obc_last_name?.value
                }
                email={
                  filteredOverviewData?.[0]?.obc_assigned?.value != null
                    ? filteredOverviewData?.[0]?.obc_assigned?.value
                    : userEmail
                }
                passport={filteredOverviewData?.[0]?.obc_passport_no?.value}
                assignmentId={filteredOverviewData?.[0]?.uniqueId}
                status={status}
              />
            }
          />
          <Route
            path="/bank_details"
            element={
              <ObcBankDetails
                email={
                  filteredOverviewData?.[0]?.obc_assigned?.value != null
                    ? filteredOverviewData?.[0]?.obc_assigned?.value
                    : userEmail
                }
              />
            }
          />
        </Routes>
      </div>
    </div>
  );
}

export default ObcApp;
